import { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { globeAPI } from '../lib/api';

// Self-hosted under /textures/ (frontend/public/) — same-origin avoids
// CORS / abort races. `earth-live.jpg` is TODAY's NASA MODIS Terra
// true-color composite, refreshed every 6h server-side and composited
// over the Blue Marble base so polar-orbit swath gaps don't show as
// vertical stripes. `earth-day.jpg` is the pristine NASA Blue Marble
// fallback (244 KB, gap-free).
const EARTH_TEXTURE_LIVE = '/textures/earth-live.jpg';
const EARTH_TEXTURE = '/textures/earth-day.jpg';
const EARTH_TEXTURE_FALLBACK = '/textures/earth-blue-marble.jpg';
const EARTH_BUMP = '/textures/earth-topology.png';
const EARTH_CLOUDS = '/textures/earth-clouds.png';
const RADIUS = 1.8;

function latLngToVec3(lat, lng, r = RADIUS) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

/**
 * Great-circle arc between two lat/lng points, sampled at `samples` points,
 * lifted parabolically above the sphere surface at midpoint.
 *
 * Method: interpolate via slerp on the sphere (great-circle = shortest path on
 * curved surface), then scale each sample outward by (1 + lift * sin(π * t))
 * to push the arc above the globe at midpoint (t=0.5). Reads as "movement
 * through space" — matches the cinematic brief.
 */
function greatCircleArcPoints(lat1, lng1, lat2, lng2, baseRadius, liftFactor, samples = 48) {
  const p1 = latLngToVec3(lat1, lng1, baseRadius);
  const p2 = latLngToVec3(lat2, lng2, baseRadius);
  const omega = p1.angleTo(p2);
  const sinOmega = Math.sin(omega);
  const out = [];
  if (omega < 1e-4) {
    // Same point — return single vertex to skip rendering
    out.push(p1.clone());
    return out;
  }
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const a = Math.sin((1 - t) * omega) / sinOmega;
    const b = Math.sin(t * omega) / sinOmega;
    const p = new THREE.Vector3(
      p1.x * a + p2.x * b,
      p1.y * a + p2.y * b,
      p1.z * a + p2.z * b,
    );
    // Lift: parabolic, max at midpoint. liftFactor ≈ 0.25 reads as a gentle
    // hop, 0.4 as a dramatic arc; 0.3 is the cinematic sweet spot.
    const lift = 1 + liftFactor * Math.sin(Math.PI * t);
    p.normalize().multiplyScalar(baseRadius * lift);
    out.push(p);
  }
  return out;
}

/**
 * Arrival pulse — a thin gold ring expanding from a destination point,
 * normal-aligned to the sphere surface (not screen-aligned), so it reads
 * as a pulse on the surface of the Earth, not a flat lens-flare.
 */
function spawnArrivalPulse(pulsesGroup, destPoint, elapsed) {
  // Ring geometry aligned to the local tangent plane at the destination.
  const ringGeo = new THREE.RingGeometry(0.02, 0.035, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xFFD700,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.position.copy(destPoint);
  // Orient so the ring is tangent to the sphere (normal = destPoint direction)
  ring.lookAt(new THREE.Vector3(0, 0, 0));
  ring.userData = { startTime: elapsed };
  pulsesGroup.add(ring);
}

export default function MiniGlobe({
  nodes = [],
  beacons = [],
  onNodeClick = () => {},
  currentYear = 2026,
  // ── Social layer (Apr 29 2026 batch #2) ───────────────────────
  // Optional props that overlay live data on top of the static globe.
  // friendArcs: [{from_lat, from_lon, to_lat, to_lon, name, relation}]
  // activeUsers: [{lat, lon, is_authenticated, country, city}]
  // userHeat:    [{lat, lon, visit_count}]
  friendArcs = [],
  activeUsers = [],
  userHeat = [],
}) {
  const containerRef = useRef(null);
  const initDone = useRef(false);
  const frameRef = useRef(null);
  const globeGroupRef = useRef(null);
  const nodesGroupRef = useRef(null);
  const arcsGroupRef = useRef(null);
  const journeysGroupRef = useRef(null);
  const pulsesGroupRef = useRef(null);
  const bordersGroupRef = useRef(null);
  // Cinematic playback state — beaconId currently being auto-framed + replayed,
  // its target rotation (quaternion-ish via Euler y), and the start time of playback.
  const playbackRef = useRef(null); // { beaconId, startedAt, targetRotY, initialRotY, pts }
  const startJourneyPlaybackRef = useRef(null);
  // Current-year for timeline↔globe dialogue; ref so the RAF loop reads freshly
  // without re-triggering the big nodes-useEffect on every year tick.
  const currentYearRef = useRef(currentYear);
  // Per-particle previous-t so we can detect segment completion (wrap) and spawn arrival pulses.
  const particleTRef = useRef(new WeakMap());
  // Apr 30 2026 — Lead's call: "Begin Your Journey doesn't rotate to
  // Africa." East African Rift ≈ lon 35°E. With latLngToVec3's
  // theta=(lng+180)°, lon 35°E sits at angle ~−35° in the xz plane;
  // a globe rotation of ~2.0 rad (≈115°) brings it forward to face
  // the camera. Slight downward tilt anchors the equatorial belt.
  const rotY = useRef(2.0);
  const rotX = useRef(0.10);
  const targetRotY = useRef(null);
  const lastBorderYear = useRef(null);

  useEffect(() => {
    if (!containerRef.current || initDone.current) return;

    const initTimer = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w === 0 || h === 0) return;
      if (initDone.current) return;
      initDone.current = true;

      // Scene
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
      camera.position.set(0, 0, 5.2);

      // Apr 30 2026 iter_64 per Lead's laptop crash report: "Uncaught
      // runtime errors: Error creating WebGL context." We now CATCH the
      // failure — some laptops (integrated GPU, old drivers, or hitting
      // Chrome's 16-context limit) can't spin up another WebGL context.
      // Instead of blowing up the whole page, we paint a static Blue
      // Marble PNG in the same slot so the HUD still reads.
      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, failIfMajorPerformanceCaveat: false, powerPreference: 'low-power' });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[MiniGlobe] WebGL unavailable, falling back to static Earth', err);
        const img = document.createElement('img');
        img.src = '/textures/earth-day.jpg';
        img.alt = 'Earth';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.style.borderRadius = '50%';
        img.setAttribute('data-testid', 'mini-globe-fallback');
        el.appendChild(img);
        initDone.current = false; // allow retry on re-mount
        return;
      }
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(0x000000, 0);
      // Force block-level canvas so no inline baseline gap creeps in
      renderer.domElement.style.display = 'block';
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      el.appendChild(renderer.domElement);

      // Lighting — warm key light + cool fill for physical globe feel
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
      scene.add(ambientLight);
      const keyLight = new THREE.DirectionalLight(0xfff5e0, 1.2);
      keyLight.position.set(5, 3, 5);
      scene.add(keyLight);
      const fillLight = new THREE.DirectionalLight(0xb0c4de, 0.3);
      fillLight.position.set(-3, -1, 2);
      scene.add(fillLight);
      const rimLight = new THREE.DirectionalLight(0x88ccff, 0.2);
      rimLight.position.set(0, 0, -5);
      scene.add(rimLight);

      // Globe group
      const globeGroup = new THREE.Group();
      globeGroupRef.current = globeGroup;
      scene.add(globeGroup);

      // Earth sphere with realistic texture — vanilla `new Image()` upload
      // path (see PopulatedGlobe.js for the long story). TextureLoader's
      // race with the image-decode pipeline was uploading a 1×1 default
      // texture and leaving the sphere as flat color; the bypass is robust.
      const earthGeo = new THREE.SphereGeometry(RADIUS, 64, 64);
      const earthMat = new THREE.MeshPhongMaterial({
        color: new THREE.Color(0x1f5b8c), // ocean-blue placeholder before texture decodes
        specular: new THREE.Color(0x333333),
        shininess: 15,
      });
      const earthMesh = new THREE.Mesh(earthGeo, earthMat);
      globeGroup.add(earthMesh);

      // Day-side photographic texture — Apr 29 2026: weather layer
      // disabled by Lead's call. Loads the canonical NASA Blue Marble
      // directly (gap-free, photorealistic). The live MODIS path
      // (`earth-live.jpg`) is preserved in the codebase for future
      // re-activation but never queried.
      const loader = new THREE.TextureLoader();
      const earthTex = loader.load(EARTH_TEXTURE);
      earthTex.colorSpace = THREE.SRGBColorSpace;
      earthMat.map = earthTex;
      earthMat.color = new THREE.Color(0xffffff);

      const bumpTex = loader.load(EARTH_BUMP);
      earthMat.bumpMap = bumpTex;
      earthMat.bumpScale = 0.04;
      earthMat.needsUpdate = true;

      // ── Clouds layer ── slightly larger sphere with the alpha cloud
      // texture, drifting slowly westward. Makes the planet feel ALIVE.
      const cloudsGeo = new THREE.SphereGeometry(RADIUS * 1.005, 64, 64);
      const cloudsMat = new THREE.MeshPhongMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      });
      const cloudsMesh = new THREE.Mesh(cloudsGeo, cloudsMat);
      cloudsMesh.visible = false; // wait for texture so we don't show a white shell
      globeGroup.add(cloudsMesh);
      const cloudsImg = new Image();
      cloudsImg.src = EARTH_CLOUDS;
      const cloudsTex = loader.load(EARTH_CLOUDS, () => { cloudsMesh.visible = true; });
      cloudsTex.colorSpace = THREE.SRGBColorSpace;
      cloudsMat.map = cloudsTex;
      cloudsMat.alphaMap = cloudsTex;
      cloudsMat.needsUpdate = true;
      void cloudsImg;

      // Thin atmosphere rim
      const atmosGeo = new THREE.SphereGeometry(RADIUS * 1.025, 64, 64);
      const atmosMat = new THREE.MeshPhongMaterial({
        color: 0x4488ff,
        transparent: true,
        opacity: 0.10,
        side: THREE.FrontSide,
      });
      globeGroup.add(new THREE.Mesh(atmosGeo, atmosMat));

      // Borders group — dynamically updated based on timeline year
      const bordersGroup = new THREE.Group();
      bordersGroupRef.current = bordersGroup;
      globeGroup.add(bordersGroup);

      // Nodes group
      const nodesGroup = new THREE.Group();
      nodesGroupRef.current = nodesGroup;
      globeGroup.add(nodesGroup);

      // Arcs group (flight paths)
      const arcsGroup = new THREE.Group();
      arcsGroupRef.current = arcsGroup;
      globeGroup.add(arcsGroup);

      // Journeys group — multi-segment ancestor journeys with particle flow
      const journeysGroup = new THREE.Group();
      journeysGroupRef.current = journeysGroup;
      globeGroup.add(journeysGroup);

      // Pulses group — arrival rings at destinations
      const pulsesGroup = new THREE.Group();
      pulsesGroupRef.current = pulsesGroup;
      globeGroup.add(pulsesGroup);

      // Clock
      const clock = new THREE.Clock();
      clock.start();

      // Animation
      function animate() {
        const elapsed = clock.getElapsedTime();
        // Sync rotation or auto-rotate
        if (targetRotY.current !== null) {
          rotY.current += (targetRotY.current - rotY.current) * 0.05;
          if (Math.abs(targetRotY.current - rotY.current) < 0.01) targetRotY.current = null;
        } else if (!dragging) {
          rotY.current += 0.003;
        }
        globeGroup.rotation.y = rotY.current;
        globeGroup.rotation.x = rotX.current;

        // Cloud drift — slightly faster than Earth so they look like
        // weather systems sliding overhead. Counter-rotate against the
        // earth so the visual offset reads as motion.
        cloudsMesh.rotation.y = elapsed * 0.012;

        // Pulse nodes
        nodesGroup.children.forEach(child => {
          if (child.userData?.isGlow) {
            child.material.opacity = 0.25 + Math.sin(elapsed * 2.5 + child.userData.phase) * 0.2;
          }
        });

        // Journey particle flow — each arc has particles that orbit the curve.
        // Three enhancements active during the RAF tick:
        //   (1) Cinematic playback — scripted t per segment (leg 1, leg 2, leg 3…)
        //   (2) Timeline↔Globe dialogue — arcs + particles bloom when currentYear
        //       is inside the ancestor's [birth_year, death_year] span
        //   (3) Arrival pulse spawner — on segment-completion wrap, spawn a
        //       gold ring at the destination
        const nowMs = performance.now();
        const playback = playbackRef.current;
        const year = currentYearRef.current;

        if (journeysGroupRef.current) {
          // First pass: update arc opacity based on lifespan + playback state.
          journeysGroupRef.current.traverse((child) => {
            if (child.userData?.isJourneyArc) {
              const { birthY, deathY, baseOpacity, beaconId } = child.userData;
              let opacity = baseOpacity ?? 0.45;
              // Lifespan bloom — breathe brighter when scrub cursor is inside life
              if (year != null && Number.isFinite(birthY) && Number.isFinite(deathY) && year >= birthY && year <= deathY) {
                const breath = 0.7 + 0.3 * Math.sin(elapsed * 1.8);
                opacity = Math.min(1, baseOpacity + 0.4 * breath);
              }
              // Further boost if this arc's beacon is the one in cinematic playback
              if (playback && playback.beaconId === beaconId) {
                opacity = Math.min(1, opacity + 0.25);
              }
              if (child.material) child.material.opacity = opacity;
            }
          });

          // Second pass: particle + halo position/opacity.
          journeysGroupRef.current.traverse((child) => {
            if (child.userData?.isParticle) {
              const { points, speed, offset, segmentIndex, totalSegments, beaconId, birthY, deathY, destinationPoint, isHalo } = child.userData;
              let t;
              if (playback && playback.beaconId === beaconId) {
                // Scripted pass: each segment plays sequentially across duration.
                // Segment i is active during [i/N, (i+1)/N]; we map that window
                // linearly into this particle's t ∈ [0, 1].
                const pbProgress = Math.min(1, (nowMs - playback.startedAt) / playback.duration);
                const segStart = segmentIndex / totalSegments;
                const segEnd = (segmentIndex + 1) / totalSegments;
                if (pbProgress <= segStart) t = 0;
                else if (pbProgress >= segEnd) t = 1;
                else t = (pbProgress - segStart) / (segEnd - segStart);
              } else {
                t = ((elapsed * speed + offset) % 1);
              }
              const idx = Math.min(points.length - 1, Math.max(0, Math.floor(t * points.length)));
              const p = points[idx];
              if (p) child.position.copy(p);
              const fade = Math.sin(Math.max(0, Math.min(1, t)) * Math.PI);
              let alpha = (isHalo ? 0.28 : 0.35) + fade * (isHalo ? 0.4 : 0.65);
              // Lifespan boost on particles too
              if (year != null && Number.isFinite(birthY) && Number.isFinite(deathY) && year >= birthY && year <= deathY) {
                alpha = Math.min(1, alpha + 0.2);
              }
              if (child.material) child.material.opacity = alpha;

              // Arrival pulse spawner — detect segment completion (t wrap) and
              // on first particle only (skip halo so we don't double-fire).
              if (!isHalo) {
                const prevT = particleTRef.current.get(child) ?? t;
                // Continuous-loop completion
                if (prevT > 0.92 && t < 0.08 && destinationPoint && pulsesGroupRef.current) {
                  spawnArrivalPulse(pulsesGroupRef.current, destinationPoint, elapsed);
                }
                // Playback-mode arrival at segment end (prevT<1, t=1)
                if (playback && playback.beaconId === beaconId && prevT < 1 && t >= 0.999 && destinationPoint && pulsesGroupRef.current) {
                  spawnArrivalPulse(pulsesGroupRef.current, destinationPoint, elapsed);
                }
                particleTRef.current.set(child, t);
              }
            }
          });
        }

        // Arrival pulses — expanding rings; remove once fully faded
        if (pulsesGroupRef.current) {
          const toRemove = [];
          pulsesGroupRef.current.children.forEach((ring) => {
            if (!ring.userData) return;
            const age = elapsed - ring.userData.startTime;
            const duration = 1.6;
            if (age > duration) {
              toRemove.push(ring);
              return;
            }
            const k = age / duration;
            const targetScale = 1 + 2.5 * k;
            ring.scale.set(targetScale, targetScale, targetScale);
            if (ring.material) ring.material.opacity = (1 - k) * 0.8;
          });
          toRemove.forEach((r) => {
            pulsesGroupRef.current.remove(r);
            if (r.geometry) r.geometry.dispose();
            if (r.material) r.material.dispose();
          });
        }

        renderer.render(scene, camera);
        frameRef.current = requestAnimationFrame(animate);
      }
      frameRef.current = requestAnimationFrame(animate);

      // Resize — window + ResizeObserver on the container so flex/responsive
      // sizing never leaves the canvas stretched or offset
      const handleResize = () => {
        const nw = el.clientWidth;
        const nh = el.clientHeight;
        if (nw > 0 && nh > 0) {
          camera.aspect = nw / nh;
          camera.updateProjectionMatrix();
          renderer.setSize(nw, nh, false);
        }
      };
      window.addEventListener('resize', handleResize);
      let ro = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => handleResize());
        ro.observe(el);
      }

      // Drag rotation — horizontal AND vertical
      let dragging = false;
      let prevX = 0;
      let prevY = 0;
      const onDown = (e) => {
        dragging = true;
        prevX = e.clientX || e.touches?.[0]?.clientX || 0;
        prevY = e.clientY || e.touches?.[0]?.clientY || 0;
        // Subtle whoosh on drag-start — feels tactile, not annoying
        try { import('../lib/sfx').then((sfx) => sfx.playGlobeWhoosh(0.6)).catch(() => {}); } catch {}
      };
      const onMove = (e) => {
        if (!dragging) return;
        const x = e.clientX || e.touches?.[0]?.clientX || 0;
        const y = e.clientY || e.touches?.[0]?.clientY || 0;
        rotY.current += (x - prevX) * 0.008;
        rotX.current += (y - prevY) * 0.008;
        rotX.current = Math.max(-1.2, Math.min(1.2, rotX.current));
        prevX = x;
        prevY = y;
        targetRotY.current = null; // cancel timeline sync when user drags
      };
      const onUp = () => { dragging = false; };

      // Scroll-to-zoom — moves the camera in/out within sane bounds so the
      // user can dive close on a hemisphere or pull back for the full globe.
      const onWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.18 : -0.18;
        camera.position.z = Math.max(2.7, Math.min(8.5, camera.position.z + delta));
      };

      // Click on nodes OR journey waypoints/arcs
      const raycaster = new THREE.Raycaster();
      const mouseVec = new THREE.Vector2();
      const onClick = (e) => {
        const rect = el.getBoundingClientRect();
        mouseVec.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouseVec.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(mouseVec, camera);
        // Priority: journey hit targets first (they're the cinematic entry point)
        if (journeysGroupRef.current) {
          const jHits = raycaster.intersectObjects(journeysGroupRef.current.children, false);
          for (const hit of jHits) {
            if (hit.object.userData?.isJourneyClickTarget && hit.object.userData?.beaconId) {
              // Trigger cinematic playback + auto-frame
              startJourneyPlaybackRef.current?.(hit.object.userData.beaconId);
              return;
            }
          }
        }
        const hits = raycaster.intersectObjects(nodesGroup.children, false);
        for (const hit of hits) {
          if (hit.object.userData?.lat !== undefined && !hit.object.userData?.isGlow) {
            onNodeClick(hit.object.userData);
            break;
          }
        }
      };

      // ─── Mobile tap-and-hold beacon playback ───
      // On touchstart, record position + time + start a 600ms hold timer that
      // raycasts and triggers playback if the user hasn't moved much. Cancel
      // the timer on touchmove or quick touchend.
      let holdTimer = null;
      let holdStart = null;
      const HOLD_MS = 600;
      const HOLD_MOVE_TOLERANCE = 8; // px
      const onTouchStartHold = (e) => {
        if (!e.touches || e.touches.length !== 1) return;
        const t0 = e.touches[0];
        holdStart = { x: t0.clientX, y: t0.clientY, t: Date.now() };
        if (holdTimer) clearTimeout(holdTimer);
        holdTimer = setTimeout(() => {
          // Fire if still holding (touchend not yet called)
          if (!holdStart) return;
          const rect = el.getBoundingClientRect();
          mouseVec.x = ((holdStart.x - rect.left) / rect.width) * 2 - 1;
          mouseVec.y = -((holdStart.y - rect.top) / rect.height) * 2 + 1;
          raycaster.setFromCamera(mouseVec, camera);
          if (journeysGroupRef.current) {
            const jHits = raycaster.intersectObjects(journeysGroupRef.current.children, false);
            for (const hit of jHits) {
              if (hit.object.userData?.isJourneyClickTarget && hit.object.userData?.beaconId) {
                // Haptic feedback if available — adds tactility to the gesture
                try { if (navigator.vibrate) navigator.vibrate(30); } catch {}
                startJourneyPlaybackRef.current?.(hit.object.userData.beaconId);
                return;
              }
            }
          }
        }, HOLD_MS);
      };
      const onTouchMoveHold = (e) => {
        if (!holdStart || !e.touches || e.touches.length !== 1) return;
        const t0 = e.touches[0];
        const dx = t0.clientX - holdStart.x;
        const dy = t0.clientY - holdStart.y;
        if (Math.sqrt(dx * dx + dy * dy) > HOLD_MOVE_TOLERANCE) {
          // User is dragging — abort the long-press so it doesn't fire
          if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
          holdStart = null;
        }
      };
      const onTouchEndHold = () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        holdStart = null;
      };

      el.addEventListener('mousedown', onDown);
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseup', onUp);
      el.addEventListener('mouseleave', onUp);
      el.addEventListener('wheel', onWheel, { passive: false });
      el.addEventListener('touchstart', onDown, { passive: true });
      el.addEventListener('touchmove', onMove, { passive: true });
      el.addEventListener('touchend', onUp);
      el.addEventListener('touchstart', onTouchStartHold, { passive: true });
      el.addEventListener('touchmove', onTouchMoveHold, { passive: true });
      el.addEventListener('touchend', onTouchEndHold);
      el.addEventListener('touchcancel', onTouchEndHold);
      el.addEventListener('click', onClick);

      // Cleanup
      return () => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        window.removeEventListener('resize', handleResize);
        if (ro) ro.disconnect();
        el.removeEventListener('mousedown', onDown);
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseup', onUp);
        el.removeEventListener('mouseleave', onUp);
        el.removeEventListener('wheel', onWheel);
        el.removeEventListener('touchstart', onDown);
        el.removeEventListener('touchmove', onMove);
        el.removeEventListener('touchend', onUp);
        el.removeEventListener('touchstart', onTouchStartHold);
        el.removeEventListener('touchmove', onTouchMoveHold);
        el.removeEventListener('touchend', onTouchEndHold);
        el.removeEventListener('touchcancel', onTouchEndHold);
        if (holdTimer) clearTimeout(holdTimer);
        el.removeEventListener('click', onClick);
        if (renderer.domElement && el.contains(renderer.domElement)) {
          el.removeChild(renderer.domElement);
        }
        renderer.dispose();
      };
    }, 150);

    return () => clearTimeout(initTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update node markers + flight path arcs
  useEffect(() => {
    if (!nodesGroupRef.current) return;
    // Clear nodes
    while (nodesGroupRef.current.children.length > 0) {
      const child = nodesGroupRef.current.children[0];
      nodesGroupRef.current.remove(child);
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    }
    // Clear arcs
    if (arcsGroupRef.current) {
      while (arcsGroupRef.current.children.length > 0) {
        const child = arcsGroupRef.current.children[0];
        arcsGroupRef.current.remove(child);
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      }
    }

    // Find home node (family beacon or first family node)
    const homeNode = nodes.find(n => n.type === 'family_beacon');

    nodes.forEach((node, idx) => {
      const pos = latLngToVec3(node.lat, node.lng, RADIUS + 0.025);
      const isOrigin = node.type === 'origin';
      const isFamily = node.type === 'family_beacon';
      const color = (isOrigin || isFamily) ? 0xFFD700 : 0x00F0FF;

      // Pin dot
      const geo = new THREE.SphereGeometry(0.045, 10, 10);
      const mat = new THREE.MeshBasicMaterial({ color });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(pos);
      mesh.userData = { ...node };
      nodesGroupRef.current.add(mesh);

      // Glow halo
      const glowGeo = new THREE.SphereGeometry(0.08, 10, 10);
      const glowMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3 });
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.copy(pos);
      glow.userData = { isGlow: true, phase: idx * 0.7 };
      nodesGroupRef.current.add(glow);

      // Family beacon pillar
      if (isFamily) {
        const pGeo = new THREE.CylinderGeometry(0.006, 0.006, 0.3, 6);
        const pMat = new THREE.MeshBasicMaterial({ color: 0xFFD700, transparent: true, opacity: 0.5 });
        const pillar = new THREE.Mesh(pGeo, pMat);
        const pPos = latLngToVec3(node.lat, node.lng, RADIUS + 0.18);
        pillar.position.copy(pPos);
        pillar.lookAt(0, 0, 0);
        pillar.rotateX(Math.PI / 2);
        nodesGroupRef.current.add(pillar);
      }

      // Flight path arc from home to this chain node
      if (homeNode && !isFamily && arcsGroupRef.current && node.lat != null && homeNode.lat != null) {
        const startPos = latLngToVec3(homeNode.lat, homeNode.lng, RADIUS + 0.03);
        const endPos = latLngToVec3(node.lat, node.lng, RADIUS + 0.03);
        const midPoint = startPos.clone().add(endPos).multiplyScalar(0.5);
        midPoint.normalize().multiplyScalar(RADIUS + 0.4); // arc height above globe

        const curve = new THREE.QuadraticBezierCurve3(startPos, midPoint, endPos);
        const arcPoints = curve.getPoints(30);
        const arcGeo = new THREE.BufferGeometry().setFromPoints(arcPoints);
        const arcMat = new THREE.LineBasicMaterial({ color: 0x00F0FF, transparent: true, opacity: 0.25 });
        const arcLine = new THREE.Line(arcGeo, arcMat);
        arcsGroupRef.current.add(arcLine);
      }
    });

    // ─── Journey arcs — multi-segment ancestor journeys with particle flow ───
    // Each segment between consecutive journey_points becomes a great-circle arc
    // lifted above the globe surface. Gold particle animates along each arc.
    if (journeysGroupRef.current) {
      while (journeysGroupRef.current.children.length > 0) {
        const child = journeysGroupRef.current.children[0];
        journeysGroupRef.current.remove(child);
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      }
      const visibleBeacons = (beacons || []).filter((b) => b && b.visible !== false && Array.isArray(b.journey_points) && b.journey_points.length >= 2);
      visibleBeacons.forEach((beacon, beaconIdx) => {
        const pts = beacon.journey_points.filter((p) => p && p.lat != null && p.lng != null);
        if (pts.length < 2) return;
        // Lifespan bounds for Timeline↔Globe dialogue. Fall back to point years.
        const birthY = Number.isFinite(beacon.birth_year) ? beacon.birth_year :
                       (pts[0].year_approximate || null);
        const deathY = Number.isFinite(beacon.death_year) ? beacon.death_year :
                       (pts[pts.length - 1].year_approximate || null);

        // ── Journey click-target — invisible sphere at the first point that
        // the raycaster can hit. Visual marker already exists via the
        // waypoint sphere; this is purely a hit target with generous radius. ──
        const firstPos = latLngToVec3(pts[0].lat, pts[0].lng, RADIUS + 0.05);
        const clickGeo = new THREE.SphereGeometry(0.14, 12, 12);
        const clickMat = new THREE.MeshBasicMaterial({ color: 0xFFD700, transparent: true, opacity: 0.0001 });
        const clickTarget = new THREE.Mesh(clickGeo, clickMat);
        clickTarget.position.copy(firstPos);
        clickTarget.userData = { isJourneyClickTarget: true, beaconId: beacon.id };
        journeysGroupRef.current.add(clickTarget);

        for (let i = 0; i < pts.length - 1; i++) {
          const a = pts[i];
          const b = pts[i + 1];
          const curvePoints = greatCircleArcPoints(a.lat, a.lng, b.lat, b.lng, RADIUS + 0.035, 0.28, 48);
          if (curvePoints.length < 2) continue;
          const geo = new THREE.BufferGeometry().setFromPoints(curvePoints);
          const mat = new THREE.LineBasicMaterial({ color: 0xFFD700, transparent: true, opacity: 0.45 });
          const line = new THREE.Line(geo, mat);
          // Tag the arc with lifespan so the RAF loop can light it when the
          // scrub year is inside the ancestor's life.
          line.userData = {
            isJourneyArc: true,
            beaconId: beacon.id,
            segmentIndex: i,
            birthY,
            deathY,
            baseOpacity: 0.45,
            destinationPoint: curvePoints[curvePoints.length - 1].clone(),
          };
          journeysGroupRef.current.add(line);

          // Waypoint marker at each intermediate journey_point (not start/end)
          if (i > 0) {
            const wpGeo = new THREE.SphereGeometry(0.03, 8, 8);
            const wpMat = new THREE.MeshBasicMaterial({ color: 0xFFD700, transparent: true, opacity: 0.85 });
            const wp = new THREE.Mesh(wpGeo, wpMat);
            wp.position.copy(latLngToVec3(a.lat, a.lng, RADIUS + 0.04));
            wp.userData = { isWaypoint: true, beaconId: beacon.id };
            journeysGroupRef.current.add(wp);
          }

          // Particle — a tiny glowing sphere that flows along the arc repeatedly
          const particleGeo = new THREE.SphereGeometry(0.045, 8, 8);
          const particleMat = new THREE.MeshBasicMaterial({ color: 0xFFE580, transparent: true, opacity: 0.85 });
          const particle = new THREE.Mesh(particleGeo, particleMat);
          particle.userData = {
            isParticle: true,
            points: curvePoints,
            speed: 0.25,  // seconds⁻¹ (4 sec per full traversal)
            offset: (beaconIdx * 0.17 + i * 0.33) % 1,
            beaconId: beacon.id,
            segmentIndex: i,
            totalSegments: pts.length - 1,
            birthY,
            deathY,
            destinationPoint: curvePoints[curvePoints.length - 1].clone(),
          };
          particle.position.copy(curvePoints[0]);
          journeysGroupRef.current.add(particle);

          // Particle glow halo (soft gold bloom travels with particle)
          const haloGeo = new THREE.SphereGeometry(0.09, 8, 8);
          const haloMat = new THREE.MeshBasicMaterial({ color: 0xFFD700, transparent: true, opacity: 0.28 });
          const halo = new THREE.Mesh(haloGeo, haloMat);
          halo.userData = {
            isParticle: true,
            isHalo: true,
            points: curvePoints,
            speed: 0.25,
            offset: particle.userData.offset,
            beaconId: beacon.id,
            segmentIndex: i,
            totalSegments: pts.length - 1,
            birthY,
            deathY,
          };
          journeysGroupRef.current.add(halo);
        }
      });
    }
  }, [nodes, beacons]);

  // Fetch and render borders based on current year
  useEffect(() => {
    if (!bordersGroupRef.current) return;
    // Debounce — only fetch if year changed significantly (every 50 years)
    const roundedYear = Math.round(currentYear / 50) * 50;
    if (roundedYear === lastBorderYear.current) return;
    lastBorderYear.current = roundedYear;

    const fetchBorders = async () => {
      try {
        const res = await globeAPI.borders(roundedYear);
        const geojson = res.data;
        if (!geojson?.features) return;

        // Clear existing borders
        while (bordersGroupRef.current.children.length > 0) {
          const child = bordersGroupRef.current.children[0];
          bordersGroupRef.current.remove(child);
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        }

        // Render new borders
        geojson.features.forEach(feature => {
          if (!feature.geometry) return;

          const processRing = (ring) => {
            const pts = [];
            for (let i = 0; i < ring.length; i++) {
              const [lng, lat] = ring[i];
              if (typeof lat === 'number' && typeof lng === 'number') {
                pts.push(latLngToVec3(lat, lng, RADIUS + 0.003));
              }
            }
            if (pts.length < 2) return;
            const geo2 = new THREE.BufferGeometry();
            const positions2 = new Float32Array(pts.length * 3);
            pts.forEach((p, j) => {
              positions2[j * 3] = p.x;
              positions2[j * 3 + 1] = p.y;
              positions2[j * 3 + 2] = p.z;
            });
            geo2.setAttribute('position', new THREE.BufferAttribute(positions2, 3));
            const mat2 = new THREE.LineBasicMaterial({ color: 0x00F0FF, transparent: true, opacity: 0.3 });
            bordersGroupRef.current.add(new THREE.Line(geo2, mat2));
          };

          if (feature.geometry.type === 'Polygon') {
            feature.geometry.coordinates.forEach(processRing);
          } else if (feature.geometry.type === 'MultiPolygon') {
            feature.geometry.coordinates.forEach(poly => poly.forEach(processRing));
          }
        });
      } catch {
        // silently fail — borders are progressive enhancement
      }
    };

    fetchBorders();
  }, [currentYear]);

  // Sync globe rotation with timeline year
  useEffect(() => {
    currentYearRef.current = currentYear;
    // Don't force-rotate if cinematic playback is running — let it finish.
    if (playbackRef.current) return;
    const MIN_YEAR = -3000;
    const MAX_YEAR = 2026;
    const pct = (currentYear - MIN_YEAR) / (MAX_YEAR - MIN_YEAR);
    // Spin from Middle East (lng 35) through Europe (0) to Americas (-90)
    const targetLng = 35 - pct * 125;
    const targetRot = -(targetLng + 180) * (Math.PI / 180);
    targetRotY.current = targetRot;
  }, [currentYear]);

  // Cinematic playback trigger — stored in a ref so the click handler
  // (registered inside the big scene-setup useEffect) can invoke it without
  // re-binding every render.
  useEffect(() => {
    let clearTimer = null;
    startJourneyPlaybackRef.current = (beaconId) => {
      const beacon = (beacons || []).find((b) => b.id === beaconId);
      if (!beacon || !Array.isArray(beacon.journey_points) || beacon.journey_points.length < 2) return;
      const pts = beacon.journey_points.filter((p) => p && p.lat != null && p.lng != null);
      if (pts.length < 2) return;
      // Aim at the midpoint of the full journey — average lng gives a good
      // framing target so the arc reads across the visible hemisphere.
      const avgLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
      const framingRotY = -(avgLng + 180) * (Math.PI / 180);
      playbackRef.current = {
        beaconId,
        startedAt: performance.now(),
        duration: 4500,  // 4.5s total replay across all segments
        targetRotY: framingRotY,
        segmentCount: pts.length - 1,
      };
      // Suppress the timeline sync during playback; set rotation target to framing
      targetRotY.current = framingRotY;
      // Auto-clear after duration so scrub resumes naturally
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(() => { playbackRef.current = null; clearTimer = null; }, 5500);
    };
    // External trigger — Follow-this-ancestor CTA + future surfaces dispatch this.
    const onTriggerPlayback = (e) => {
      const beaconId = e?.detail?.beaconId;
      if (beaconId && startJourneyPlaybackRef.current) {
        startJourneyPlaybackRef.current(beaconId);
      }
    };
    window.addEventListener('tsogn:trigger-playback', onTriggerPlayback);
    return () => {
      if (clearTimer) clearTimeout(clearTimer);
      startJourneyPlaybackRef.current = null;
      window.removeEventListener('tsogn:trigger-playback', onTriggerPlayback);
    };
  }, [beacons]);

  // ── Social-layer effect (Apr 29 2026 batch #2) ───────────────────
  // Mounts pulsing dots for activeUsers, gold arcs for friendArcs, and
  // copper dots for the user's own heat signature. Re-runs when the
  // props change (every ~30s when useGlobePresence returns new data).
  // Meshes attach to globeGroupRef so they rotate with the planet.
  useEffect(() => {
    const globeGroup = globeGroupRef.current;
    if (!globeGroup) return undefined;

    const added = [];

    // Active-earth presence dots
    activeUsers.forEach((u) => {
      if (typeof u?.lat !== 'number' || typeof u?.lon !== 'number') return;
      const pos = latLngToVec3(u.lat, u.lon, RADIUS * 1.012);
      const color = u.is_authenticated ? 0xffd700 : 0x00f0ff;
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.022, 12, 12),
        new THREE.MeshBasicMaterial({ color }),
      );
      dot.position.copy(pos);
      dot.userData = { kind: 'active-user' };
      globeGroup.add(dot);

      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 12, 12),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.32 }),
      );
      halo.position.copy(pos);
      halo.userData = { kind: 'active-user-halo' };
      globeGroup.add(halo);

      added.push(dot, halo);
    });

    // Friend arcs (current user → each family-member lat/lng)
    friendArcs.forEach((arc) => {
      if (typeof arc?.from_lat !== 'number' || typeof arc?.from_lon !== 'number') return;
      if (typeof arc?.to_lat !== 'number' || typeof arc?.to_lon !== 'number') return;
      const from = latLngToVec3(arc.from_lat, arc.from_lon, RADIUS);
      const to = latLngToVec3(arc.to_lat, arc.to_lon, RADIUS);
      const mid = from.clone().add(to).multiplyScalar(0.5);
      const dist = from.distanceTo(to);
      mid.normalize().multiplyScalar(RADIUS + dist * 0.45);
      const curve = new THREE.QuadraticBezierCurve3(from, mid, to);
      const points = curve.getPoints(64);
      const arcGeom = new THREE.BufferGeometry().setFromPoints(points);
      const arcMat = new THREE.LineBasicMaterial({
        color: 0xffd700, transparent: true, opacity: 0.85,
      });
      const arcLine = new THREE.Line(arcGeom, arcMat);
      arcLine.userData = { kind: 'friend-arc' };
      globeGroup.add(arcLine);

      const pellet = new THREE.Mesh(
        new THREE.SphereGeometry(0.026, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xffd700 }),
      );
      pellet.position.copy(to);
      pellet.userData = { kind: 'friend-pellet' };
      globeGroup.add(pellet);

      added.push(arcLine, pellet);
    });

    // User heat signature (visit-count → dot scale, copper to differentiate)
    if (userHeat.length > 0) {
      const maxCount = Math.max(...userHeat.map((h) => h.visit_count || 1));
      const scaleFor = (c) => 0.014 + 0.030 * (Math.log(1 + (c || 1)) / Math.log(1 + maxCount));
      userHeat.forEach((h) => {
        if (typeof h?.lat !== 'number' || typeof h?.lon !== 'number') return;
        const pos = latLngToVec3(h.lat, h.lon, RADIUS * 1.008);
        const r = scaleFor(h.visit_count || 1);
        const dot = new THREE.Mesh(
          new THREE.SphereGeometry(r, 10, 10),
          new THREE.MeshBasicMaterial({ color: 0xff8c42, transparent: true, opacity: 0.78 }),
        );
        dot.position.copy(pos);
        dot.userData = { kind: 'user-heat' };
        globeGroup.add(dot);
        added.push(dot);
      });
    }

    return () => {
      added.forEach((m) => {
        try {
          globeGroup.remove(m);
          if (m.geometry) m.geometry.dispose();
          if (m.material) m.material.dispose();
        } catch { /* ignore */ }
      });
    };
  }, [friendArcs, activeUsers, userHeat]);

  return (
    <div
      data-testid="mini-globe"
      ref={containerRef}
      style={{ width: '100%', height: '100%', cursor: 'grab' }}
    />
  );
}
